import { 
  useMutation, 
  MutationHookOptions, 
  OperationVariables, 
  DocumentNode
} from '@apollo/client';

interface UseGraphQLMutationOptions<TData, TVariables extends OperationVariables>
  extends MutationHookOptions<TData, TVariables> {
  successMessage?: string;
  errorMessage?: string;
  showSuccessToast?: boolean;
  showErrorToast?: boolean;
  onSuccessCallback?: (data: TData) => void;
  onErrorCallback?: (error: Error) => void;
}

export function useGraphQLMutation<
  TData = any,
  TVariables extends OperationVariables = OperationVariables
>(
  mutation: DocumentNode,
  options?: UseGraphQLMutationOptions<TData, TVariables>
) {
  const {
    successMessage,
    errorMessage,
    showSuccessToast = true,
    showErrorToast = true,
    onSuccessCallback,
    onErrorCallback,
    ...mutationOptions
  } = options || {};

  const [mutate, result] = useMutation<TData, TVariables>(mutation, {
    ...mutationOptions,
    onCompleted: (data) => {
      if (showSuccessToast && successMessage) {
        console.log('[GraphQL Mutation Success]:', successMessage);
        // Toast will be implemented by consuming apps
      }
      onSuccessCallback?.(data);
      mutationOptions.onCompleted?.(data);
    },
    onError: (error) => {
      if (showErrorToast) {
        const message = errorMessage || error.message || 'Operation failed';
        console.error('[GraphQL Mutation Error]:', message);
        // Toast will be implemented by consuming apps
      }
      onErrorCallback?.(error);
      mutationOptions.onError?.(error);
    },
  });

  return [mutate, result] as const;
}
